import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import Checkbox from 'component/Layout/Form/Checkbox';

import classNames from 'classnames';

import { useAccommodationType } from 'hooks/form/filter';
import { useAccommodationMeta } from 'hooks/form/meta';
import { useChangedFilterList } from 'hooks/form/useChangedFilterList';
import { QaIdentifier } from 'hooks/useQa';

import { toggleValueInSet } from 'utility/Toggle/toggleValueInSet';

import { SearchParam } from 'types/Search/params';

import { AccommodationCategory } from 'proto/Accommodation/AccommodationCategory';

import styles from '../Filter.module.scss';
import Segment from '../Segment';
import {
    MetaValue,
    useDisabledStatus,
} from '../Segment/__hooks/useDisabledStatus';
import FilterLabel from '../Segment/FilterLabel';
import { FilterElement } from '../Segment/types';

import getAccommodationTypeLabel from './helper/getAccommodationTypeLabel';

const AccommodationType = ({ disableWhen = MetaValue.NoResult }) => {
    const { t } = useTranslation();
    const [accommodationMeta] = useAccommodationMeta();
    const [accommodationTypeList, setAccommodationTypeList] =
        useAccommodationType();
    const { onToggle, onClear, isFilterChanged } = useChangedFilterList();
    const getDisabledStatus = useDisabledStatus(disableWhen);

    const onSelect = useCallback(
        (selection: AccommodationCategory) => {
            onToggle(selection);
            setAccommodationTypeList((current) =>
                toggleValueInSet(current, selection)
            );
        },
        [setAccommodationTypeList, onToggle]
    );

    const accommodationTypeFilter: FilterElement[] = Object.values(
        AccommodationCategory
    ).map((accommodationType) => {
        const currentTypeMeta =
            accommodationMeta?.accommodationCategory[accommodationType];
        const isChecked = accommodationTypeList.has(accommodationType);
        const label = getAccommodationTypeLabel(accommodationType, t);
        const isDisabled =
            !isChecked && currentTypeMeta && getDisabledStatus(currentTypeMeta);
        return {
            isSelected: isChecked,
            kiFilterData: {
                label,
                isSelectable: !isDisabled,
                urlKey: SearchParam.AccommodationCategoryList,
                value: accommodationType ? accommodationType.toLowerCase() : '',
                ...currentTypeMeta,
            },
            content: currentTypeMeta?.hasCount && (
                <Checkbox
                    key={accommodationType}
                    input={{
                        id: `acg-${accommodationType.toLowerCase()}-option`,
                        value: accommodationType.toLowerCase(),
                        name: accommodationType,
                    }}
                    isDisabled={isDisabled}
                    isOnlyDeselectable={
                        isChecked &&
                        currentTypeMeta?.hasCount &&
                        currentTypeMeta?.count === 0
                    }
                    onChange={() => onSelect(accommodationType)}
                    isChecked={isChecked}
                    withUXSelection
                    customClass={{
                        Wrapper: classNames({
                            [styles.Changed]:
                                isFilterChanged(accommodationType),
                        }),
                    }}
                >
                    <FilterLabel labelText={label} {...currentTypeMeta} />
                </Checkbox>
            ),
        };
    });

    const headline = t('TRANS-accommodation-type');

    return (
        <Segment
            headline={{ text: headline }}
            withDeleteLink
            qaIdentifier={QaIdentifier.Search_Filter_Section_AccommodationType}
            onDelete={() => {
                onClear(() => [...accommodationTypeList]);
                setAccommodationTypeList(new Set());
            }}
            filterList={accommodationTypeFilter}
            kiFilterGroupData={{
                refKey: SearchParam.AccommodationCategoryList,
                label: headline,
                multi: true,
            }}
        />
    );
};

export default AccommodationType;
